import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  CardHeader,
  Divider,
  Grid,
  Link,
  Typography,
  Table,
  TableCell,
  TableRow,
  TableBody,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { formatCurrency } from 'src/utils/currency';
import ChargeInfoDialog from 'src/components/PurchaseOrderDetails/modals/ChargeInfoDialog';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
  },

  cardContainer: {
    backgroundColor: theme.palette.background.dark,
  },

  fontWeightMedium: {
    fontWeight: theme.typography.fontWeightMedium,
  },

  cardHeader: {
    color: theme.palette.secondary.main,
  },

  chargeTotal: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
    marginLeft: '50px',
    color: theme.palette.common.black,

    '& span': {
      color: theme.palette.primary.light,
    },
  },

  chargeTotalDirty: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
    marginLeft: '50px',
    color: `${theme.palette.warning.main} !important`,
    fontWeight: theme.typography.fontWeightRegular,
  },

  addButton: {
    float: 'right',
    top: '-2px',
  },

  fontStyleHighlight: {
    color: theme.palette.secondary.main,
    fontWeight: 'bold',
  },

  chargeTotalStyle: {
    marginRight: '20px',
    marginTop: '4px',
    float: 'right',
  },

}));

const ChargeInfo = React.forwardRef(function forwardedChargeInfo(props, ref) {
  const {
    chargeFees,
    chargeTypes,
    purchaseOrderDetails,
    setPurchaseOrderDetails,
    providerCategory,
    canUserAddCharge,
    isChargeEditsDisabled,
  } = props;

  React.useImperativeHandle(ref, () => ({
    updateUnloadedMileage: () => handleAddRateInfo({ purchaseOrderChargeTypeId: 4 }),
    updateLoadedMileage: () => handleAddRateInfo({ purchaseOrderChargeTypeId: 3 }),
  }));

  const classes = useStyles();
  const [availableCharges, setAvailableCharges] = useState(null);
  const [appliedCharges, setAppliedCharges] = useState(null);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [isChargeReadOnly, setIsChargeReadOnly] = useState(false);

  const [isRateInfoModalDialogOpen, setIsRateInfoModalDialogOpen] = useState(false);

  useEffect(() => {
    const appliedCharges = chargeFees == null ? [] : JSON.parse(JSON.stringify(chargeFees));
    setAppliedCharges(appliedCharges);
  }, [chargeFees]);

  useEffect(() => {
    const availableCharges = chargeTypes == null ? [] :
      chargeTypes
        .filter(o => o.isInternalEditable)
        .map(o =>
          ({ ...o, formattedName: formatName(o.purchaseOrderChargeTypeName, 2), amount: 0 , categoryId: o?.categoryId}));

    setAvailableCharges(availableCharges);
  }, [chargeTypes]);

  const formatName = (name) => {
    const formattedName = name.replace(/([A-Z])/g, ' $1').trim();
    return formattedName;
  };

  function handleRateInfoModalApply(modifiedRateInfo) {
    const mutatedAppliedCharges = [...appliedCharges];
    purchaseOrderDetails.isDirty = true;
    var existingCharges = mutatedAppliedCharges
      .filter(o => o.purchaseOrderChargeTypeId == selectedCharge.purchaseOrderChargeTypeId);

    var modifiedCharge = modifiedRateInfo
      .filter(o => o.purchaseOrderChargeTypeId == selectedCharge.purchaseOrderChargeTypeId)[0];

    if (!existingCharges || existingCharges.length == 0) {
      if (modifiedCharge.amount > 0)
        mutatedAppliedCharges.push(modifiedCharge);
    } else {
      if (modifiedCharge.amount > 0) {
        existingCharges[0].amount = Number(modifiedCharge.amount);
      } else {
        const index = mutatedAppliedCharges.indexOf(existingCharges[0]);
        mutatedAppliedCharges.splice(index, 1);
      }
    }
    modifiedRateInfo.forEach(o => {
      if (o.name == 'LoadedMileage') {
        purchaseOrderDetails.MilesLoaded = Number(o.amount);
      }
      if (o.name === 'LoadedFreeMiles' && providerCategory === 'Out Of Network') {
        purchaseOrderDetails.FreeLoadedMiles = Number(o.amount);
      }
      if (o.name === 'LoadedMileageRate' && providerCategory === 'Out Of Network') {
        purchaseOrderDetails.LoadedMileageRate = Number(o.amount);
      }
      if (o.name == 'UnloadedMileage') {
        purchaseOrderDetails.MilesUnloaded = Number(o.amount);
      }
      if (o.name === 'UnloadedFreeMiles' && providerCategory === 'Out Of Network') {
        purchaseOrderDetails.FreeUnloadedMiles = Number(o.amount);
      }
      if (o.name === 'UnloadedMileageRate' && providerCategory === 'Out Of Network') {
        purchaseOrderDetails.UnloadedMileageRate = Number(o.amount);
      }
      if (o.name == 'DailyStorageRate') {
        purchaseOrderDetails.StorageDailyRate = Number(o.amount);
      }
      if (o.name == 'DaysInStorage') {
        purchaseOrderDetails.StorageDays = Number(o.amount);
      }
      if (o.name == 'SecondaryDailyStorageRate') {
        purchaseOrderDetails.SecondaryStorageDailyRate = Number(o.amount);
      }
      if (o.name == 'SecondaryDaysInStorage') {
        purchaseOrderDetails.SecondaryStorageDays = Number(o.amount);
      }
    });

    let poTotal = 0;
    mutatedAppliedCharges.forEach(o => poTotal += Number(o.amount ?? 0));
    purchaseOrderDetails.TotalPurchaseOrderAmount = poTotal;
    purchaseOrderDetails.Charges = mutatedAppliedCharges;
    setPurchaseOrderDetails({ ...purchaseOrderDetails });

    setAppliedCharges(mutatedAppliedCharges);
    setIsRateInfoModalDialogOpen(false);
    setSelectedCharge(null);
  }

  function handleAddRateInfo(charge) {
    setIsChargeReadOnly(!charge);
    if (charge) {
      const matchingChargeTypes = availableCharges
        .filter(o => o.purchaseOrderChargeTypeId == charge.purchaseOrderChargeTypeId);
      setSelectedCharge(
        matchingChargeTypes.length > 0 ?
          matchingChargeTypes[0] : null);
    } else {
      setSelectedCharge(null);
    }

    setIsRateInfoModalDialogOpen(true);
  }

  return (
    <>
      {(availableCharges) &&
        <ChargeInfoDialog
          isDialogOpen={isRateInfoModalDialogOpen}
          setIsDialogOpen={setIsRateInfoModalDialogOpen}
          handleApply={handleRateInfoModalApply}
          availableCharges={availableCharges}
          appliedCharges={appliedCharges}
          selectedCharge={selectedCharge}
          setSelectedCharge={setSelectedCharge}
          purchaseOrderDetails={purchaseOrderDetails}
          isChargeReadOnly={isChargeReadOnly}
          providerCategory={providerCategory}
        />}
      <div className={classes.cardContainer}>
        <Card className={classes.card}>
          <CardHeader
            className={classes.cardHeader}
            title={(
              <>
                Purchase Order Charge Details
                {purchaseOrderDetails?.isDirty == true ? (
                  <span className={classes.chargeTotal}>
                    Total Invoiceable Amount:
                    <span className={classes.chargeTotalDirty}>
                      {formatCurrency(purchaseOrderDetails?.TotalPurchaseOrderAmount ?? 0)}
                    </span>
                  </span>
                ) : (
                  <span className={classes.chargeTotal}>
                    Total Invoiceable Amount: <span>{formatCurrency(purchaseOrderDetails?.TotalPurchaseOrderAmount ?? 0)}</span>
                  </span>
                )}
                <Button
                  className={classes.addButton}
                  variant='contained'
                  color='primary'
                  onClick={() => handleAddRateInfo(null, 1)}
                  disabled={!canUserAddCharge}
                >
                  Add a Charge
                </Button>
              </>)}
          />
          <Divider />
          <Grid container style={{ marginTop: '16px' }} xs={12}>
            <Grid xs={8}>
              <Typography variant="body2" color="textSecondary" className={classes.fontStyleHighlight} style={{ marginLeft: '16px' }}>
                Provider Charges
              </Typography>
            </Grid>
            <Grid xs={4}>
              <Typography variant="body2" color="textSecondary" className={classes.chargeTotalStyle}>
                Total: &nbsp;
                <span className={classes.fontStyleHighlight}>
                  {formatCurrency(appliedCharges?.filter(o => o.categoryId === "1" && Number(o.amount) > 0).reduce((acc, curr) => acc + Number(curr.amount || 0), 0))}
                </span>
              </Typography>
            </Grid>
          </Grid>
          <Grid container xs={12}>
            
            {(appliedCharges) && appliedCharges.filter(o => o.categoryId === "1" && o.amount > 0).map(o => {
              return (
                <Grid item key={o.purchaseOrderChargeTypeName}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell className={classes.fontWeightMedium}>
                          {formatName(o.purchaseOrderChargeTypeName)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="textSecondary">
                            <Link
                              component='button'
                              disabled={isChargeEditsDisabled}
                              style={{
                                cursor: isChargeEditsDisabled ? 'default' : 'pointer'
                              }}
                              onClick={() => handleAddRateInfo(o, 2)}
                            >
                              {formatCurrency(o.amount)}
                            </Link>
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>
              );
            })}
          </Grid>
          {appliedCharges?.filter(o => o.categoryId === "2" && o.amount > 0).length > 0 && 
          (<><Grid container style={{marginTop: '16px'}} xs={12}>
            <Grid xs={8} >
              <Typography variant="body2" color="textSecondary" className={classes.fontStyleHighlight} style={{ marginLeft: '16px', marginTop: '8px' }}>
                Secondary Charges
              </Typography>
            </Grid>
            <Grid xs={4}>
              <Typography variant="body2" color="textSecondary" className={classes.chargeTotalStyle}>
                 Total: &nbsp;
                <span className={classes.fontStyleHighlight}>
                  {formatCurrency(  appliedCharges?.filter(o => o.categoryId === "2" && Number(o.amount) > 0).reduce((acc, curr) => acc + Number(curr.amount || 0), 0))}
                </span>
              </Typography>
            </Grid>
          </Grid>
          <Grid container xs={12}>
          {(appliedCharges) && appliedCharges.filter(o => o.categoryId === "2" && o.amount > 0).map(o => {
              return (
                <Grid item key={o.purchaseOrderChargeTypeName}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell className={classes.fontWeightMedium}>
                          {formatName(o.purchaseOrderChargeTypeName)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="textSecondary">
                            <Link
                              component='button'
                              disabled={isChargeEditsDisabled}
                              style={{
                                cursor: isChargeEditsDisabled ? 'default' : 'pointer'
                              }}
                              onClick={() => handleAddRateInfo(o, 2)}
                            >
                              {formatCurrency(o.amount)}
                            </Link>
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>
              );
            })}
          </Grid></>)}
        </Card>
      </div>
    </>
  );
});

ChargeInfo.propTypes = {
  chargeTypes: PropTypes.arrayOf(PropTypes.shape({
    purchaseOrderChargeTypeId: PropTypes.string,
    purchaseOrderChargeTypeName: PropTypes.string,
    isCalculated: PropTypes.bool,
    isExternalEditable: PropTypes.bool,
    isInternalEditable: PropTypes.bool,
  })),
  chargeFees: PropTypes.arrayOf(PropTypes.shape({
    purchaseOrderChargeId: PropTypes.number,
    poNumber: PropTypes.number,
    purchaseOrderChargeTypeId: PropTypes.string,
    purchaseOrderChargeTypeName: PropTypes.string,
    amount: PropTypes.number,
    isCalculated: PropTypes.bool,
    isExternalEditable: PropTypes.bool,
    isInternalEditable: PropTypes.bool,
  })),
  purchaseOrderDetails: PropTypes.shape({
    AmountOwed: PropTypes.number,
    LoadedMileageRate: PropTypes.number,
    MilesLoaded: PropTypes.number,
    FreeLoadedMiles: PropTypes.number,
    UnloadedMileageRate: PropTypes.number,
    MilesUnloaded: PropTypes.number,
    FreeUnloadedMiles: PropTypes.number,
    StorageDailyRate: PropTypes.number,
    StorageDays: PropTypes.number,
    isDirty: PropTypes.bool,
    TotalPurchaseOrderAmount: PropTypes.number,
    Charges: PropTypes.arrayOf(PropTypes.shape({
      purchaseOrderChargeTypeId: PropTypes.string,
      purchaseOrderChargeTypeName: PropTypes.string,
      isCalculated: PropTypes.bool,
      isExternalEditable: PropTypes.bool,
      isInternalEditable: PropTypes.bool,
    })),
  }),
  setPurchaseOrderDetails: PropTypes.func,
  providerCategory: PropTypes.string,
  canUserAddCharge: PropTypes.bool,
  isChargeEditsDisabled: PropTypes.bool,
};

ChargeInfo.defaultProps = {
  canUserAddCharge: false,
  isChargeEditsDisabled: false,
};


export default ChargeInfo;
